<script setup lang="ts">
import type { SliderRootEmits, SliderRootProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cn } from '@/lib/utils'
import { SliderRange, SliderRoot, SliderThumb, SliderTrack, useForwardPropsEmits } from 'reka-ui'
import { computed } from 'vue'

const props = defineProps<SliderRootProps & { class?: HTMLAttributes['class'], thumbClass?: HTMLAttributes['class'] }>()
const emits = defineEmits<SliderRootEmits>()

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
const pointerdown = ref(false)
const thumbClass = computed(() => {
  const base = `z-40 block h-7 w-7 cursor-grab rounded-full thumbShadow bg-background ring-offset-background transition-color focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 halo-effect transition-all duration-150`
  if (pointerdown.value) {
    return cn(base, 'transition-none cursor-grabbing border-primary', props.thumbClass)
  }
  return cn(base, props.thumbClass)
})
</script>

<template>
  <SliderRoot
    :class="cn(
      'group relative flex w-full touch-none select-none items-center data-[orientation=vertical]:flex-col data-[orientation=vertical]:w-2 data-[orientation=vertical]:h-full',
      props.class,
    )" v-bind="forwarded"
    @pointerdown="pointerdown = true"
    @pointerup="pointerdown = false"
  >
    <SliderTrack
      class="relative h-3 w-full data-[orientation=vertical]:w-2 grow overflow-hidden rounded-full bg-gray-100"
    >
      <SliderRange :class="[pointerdown && 'transition-none']" class="absolute h-full data-[orientation=vertical]:w-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-150" />
      <slot />
    </SliderTrack>
    <ClientOnly>
      <SliderThumb
        v-for="(_, key) in modelValue" :key="key"
        :class="thumbClass"
      />
    </ClientOnly>
  </SliderRoot>
</template>

<style scoped>
.thumbShadow {
  box-shadow: 4px 4px 12px rgba(85, 108, 130, 1.04);
}
.halo-effect:hover {
  box-shadow: 0 0 0px 8px oklch(from var(--primary) l c h / 0.25) !important;
}
</style>
